import { BlurEvent, ChangeEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem, DropdownSelectionEvent,
    LocationUtil, LookupModelSearchEvent, LookupModelSelectionEvent, Tab, Textbox, ValidationResult
} from "@mcleod/components";
import { Api, DatePart, DateUtil, Model, ModelRow, ModelRowType, ObjectUtil, StringUtil, Timezone } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { ModelContact, ModelZones } from "@mcleod/general";
import { AutogenLayoutStopEntry } from "./autogen/AutogenLayoutStopEntry";
import { ModelLocation } from "./models/ModelLocation";
import { OrderStop } from "./OrderStop";
import { AppointmentUtil } from "./AppointmentUtil";

export class StopEntry extends AutogenLayoutStopEntry {

    static readonly PICKUP_STOP_TYPES = ["PU", "SP"];

    public getTimezone: boolean = false;
    public isDelivery: boolean = false;
    public stopType: string = null;
    private _orderId: string = null;

    public locationChanged: () => void;
    private locationChangeListener = event => this.handleLocationChange(event);

    override onLoad() {
        this.mainDataSource.preventFocusFirstField = true;
        this.textboxPickupDate.items = [LocationUtil.DATE_ON, LocationUtil.DATE_BETWEEN];
        this.textboxPickupDate.selectedItem = LocationUtil.DATE_BETWEEN;
        this.locationLocation.addLookupModelSelectionListener(this.locationChangeListener);
        this.locationLocation.addChangeListener((event: ChangeEvent) => {
            const rowLocation: ModelRow<any> = event.newValue;
            const enableCityState = StringUtil.isEmptyString(rowLocation.data?.city_id);
            this.citystateCityStateZip.enabled = enableCityState;
            if (enableCityState) {
                this.activeRow.set("zone_id", null);
                this.textboxZoneId.displayData(this.activeRow, null, 0);
                this.textboxContactName.clear();
                this.textboxContactPhone.clear();
                this.textboxContactEmail.clear();
            }
        })

        this.textboxSchedArriveEarly.addBlurListener((event: BlurEvent) => this.stopDateOnBlur(event));
        this.textboxSchedArriveLate.addBlurListener((event: BlurEvent) => this.stopDateOnBlur(event));
        this.textboxSchedArriveEarly.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(true, false, this.textboxSchedArriveEarly, this.textboxSchedArriveLate, mode);
        };
        this.textboxSchedArriveLate.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(false, false, this.textboxSchedArriveEarly, this.textboxSchedArriveLate, mode);
        };
        this.textboxActualArrival.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(true, true, this.textboxActualArrival, this.textboxActualDeparture, mode);
        };
        this.textboxActualDeparture.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(false, true, this.textboxActualArrival, this.textboxActualDeparture, mode);
        };
    }

    private async handleLocationChange(event: LookupModelSelectionEvent) {
        const locationId = event.selection?.get("id") as string;
        this.mainDataSource.redisplayRow(0, this.activeRow ?? null, null);
        this.citystateCityStateZip["_lookupModelDataForUpdate"] = null;
        if (locationId) {
            this.textboxContactName.clear();
            this.textboxContactPhone.clear();
            this.textboxContactEmail.clear();
            const rowLocation: ModelRow<any> = await new ModelLocation().searchSingle({ id: locationId, "updating_stop": true, get_timezone: this.getTimezone }, "lme/dispatch/Location");
            if (rowLocation == null) {
                CommonDialogs.showDialog("Location [" + locationId + "] not found in database.");
                this.citystateCityStateZip.enabled = true;
            } else {
                this.textboxLocationId.quickInfoOverrideKeyValue = locationId;
                if (this.textboxPickupDate.visible) {
                    this.setupStopDateTypeByTimes(rowLocation);
                    this.setStopTypeCaptions()
                }
                this.setStopContactInfo(locationId);
                this.citystateCityStateZip.enabled = false;
                //There is logic in Location fields to set location-based model row values
                //This logic does not appear to set Address2 bc it is not part of the textCombined data
                this.mainDataSource.activeRow.set("address2", rowLocation.get("address2", null))
                if (!rowLocation.isNull("stop_timezone_id")) {
                    this.setStopTimezone(rowLocation.get("stop_timezone_id"));
                }
                const zoneResponse = await new ModelZones().searchSingle({ city_id: rowLocation.data?.city_id });
                if (zoneResponse != null) {
                    const lmModelRow = new ModelRow(this.textboxZoneId.lookupModel, false, {
                        id: zoneResponse.get("id"),
                        descr: zoneResponse.get("descr")
                    });
                    lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                    this.activeRow.setLookupModelData("zone_id", lmModelRow);
                    this.activeRow.set("zone_id", zoneResponse.get("id"));
                    this.textboxZoneId.displayData(this.activeRow, null, 0);
                }
                if (event.hasSelection()) {
                    this.locationChanged?.();
                }
            }
        } else {
            this.citystateCityStateZip.enabled = true;
            if (event.hasDeselection())
                this.citystateCityStateZip.clearAllFields();
        }
        this.citystateCityStateZip.displayData(this.activeRow ?? null, null, 0);
    }

    setupStopDateTypeByTimes(rowLocation: ModelRow<any>) {
        //If values present, ignore location
        if (this.activeRow.get("sched_arrive_early") != null) {
            this.textboxPickupDate.selectedItem = this.activeRow.get("sched_arrive_late") != null ? LocationUtil.DATE_BETWEEN : LocationUtil.DATE_ON;
            this.setStopTypeCaptions();
        } else {
            if (rowLocation) {
                this.textboxPickupDate.selectedItem = LocationUtil.getLocationDefaultStopType(rowLocation, this.activeRow.get("stop_type"));
            }
        }
    }

    textboxStopTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.isDelivery = !StopEntry.PICKUP_STOP_TYPES.includes(event.selection?.value);
        const stopType = event.target.dataSource.activeRow.get("stop_type");
        if(!this.mainDataSource.activeRow?.isNull("location_id")){
            const location_id = this.mainDataSource.activeRow?.get("location_id")
            this.updateStopLocationTimeType(stopType, location_id)
        }
        this.setStopTypeCaptions();
    }

    updateStopLocationTimeType(curStopType: string, location_id: string) {
        Model.searchSingleRecord("lme/dispatch/location", { id: location_id, "updating_stop": true }).then(rowLocation => {
            const defaultTimeType = LocationUtil.getLocationDefaultStopType(rowLocation, curStopType);
            this.textboxPickupDate.selectedItem = defaultTimeType;
        });
    }

    async setStopContactInfo(locationId: string) {
        await new ModelContact().searchSingle({ "parent_row_type": "L", "parent_row_id": locationId }).then(rowContact => {
            if (rowContact == null) {
                return;
            }
            this.mainDataSource.activeRow.setLookupModelData("contact_name", rowContact.data);
            this.mainDataSource.activeRow.set("contact_name", rowContact.data["name"]);
            this.mainDataSource.activeRow.set("phone", rowContact.data["phone"]);
            this.mainDataSource.activeRow.set("contact_email", rowContact.data["email"]);
        });
    }

    /** This is an event handler for the onChange event of textboxPickupDate. */
    textboxPickupDateOnChange(event) {
        this.stopDateTypeChange(event.newValue);
    }

    textboxContactNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.mainDataSource.activeRow.get("location_id")) {
            event.filter.parent_row_type = "L";
            event.filter.parent_row_id = this.mainDataSource.activeRow.get("location_id");
            event.filter.is_active = "Y";
        } else {
            event.preventDefault();
        }
    }

    setStopTypeCaptions() {
        const caption = this.isDelivery ? "Delivery" : "Pickup";
        const isDateWindow = this.textboxPickupDate.selectedItem?.value == LocationUtil.DATE_BETWEEN.value;
        let pickupDateCaption = caption;
        let schedEarlyCaption = isDateWindow ? `${caption} Window` : `${caption} Appointment`;

        if (this.activeRow) {
            if (LocationUtil.isViaPoint(this.activeRow.getString("stop_type"))) {
                schedEarlyCaption = this.textboxPickupDate.selectedItem.value == LocationUtil.DATE_ON.value ? "Scheduled" : "Scheduled Window";
                pickupDateCaption = "Via Point";
            }
        }

        this.textboxSchedArriveEarly.caption = schedEarlyCaption;
        this.textboxPickupDate.caption = pickupDateCaption;
    }

    private stopDateTypeChange(stopDateType: string) {
        if (LocationUtil.DATE_ON.displayValue === stopDateType) {
            this.sourceStop.activeRow.set("sched_arrive_late", null);
            this.textboxSchedArriveLate.clear();
        }
        this.panelLateArrival.visible = this.textboxSchedArriveLate.required = (LocationUtil.DATE_BETWEEN.displayValue === stopDateType);
        this.textboxSchedArriveEarly.paddingRight = (LocationUtil.DATE_ON.displayValue === stopDateType) ? this.textboxSchedArriveLate.paddingRight : null;
        this.setStopTypeCaptions();
    }

    textboxContactNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            this.mainDataSource.activeRow.setLookupModelData("contact_name", selectedRow);
            this.mainDataSource.activeRow.set("phone", selectedRow.get("phone"));
            this.mainDataSource.activeRow.set("contact_email", selectedRow.get("email"));
            this.mainDataSource.displayDataInBoundComponents();
        }
    }

    setStopTimezone(timezoneId: string) {
        const zone = Timezone.getFromAbbreviation(timezoneId);
        this.textboxActualArrival.timezone =
        this.textboxActualDeparture.timezone = zone;
    }

    /** This is an event handler for the onBlur event of citystateCityStateZip. */
    citystateCityStateZipOnBlur(event: BlurEvent) {
        if (!this.activeRow.isNull("city_id")) {
            Api.post("lme/dispatch/get-city-timezone", { city_id: this.activeRow.get("city_id") }).then(res => {
                this.setStopTimezone(res.data[0].timezone_id);
            });
            if (ObjectUtil.containsKey(this.activeRow.getChangedData(), "city_id") && this.citystateCityStateZip.enabled) {
                this.locationChanged?.();
            }
        }
    }

    public getStop() {
        return this.mainDataSource.activeRow;
    }

    public newEntry(props?) {
        this.mainDataSource.clear();
        return this.sourceStop.createBlankRow().then(row => {

            this.citystateCityStateZip.enabled = true;
            this.textboxPickupDate.selectedItem = LocationUtil.DATE_BETWEEN;

            row.set("order_id", this.orderId);
            if (props) {
                row.set({...props});
            }

            const notesRefsLayout = this.layoutStopNotesAndRefNbrs;
            const stopNoteTable = notesRefsLayout.tableStopComments;
            const refNbrTable = notesRefsLayout.tableStopReferenceNumbers;

            row.set("stop_notes", stopNoteTable.data);
            row.set("reference_numbers", refNbrTable.data);
            row.resetOriginalData();
            this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [row]);
            [stopNoteTable, refNbrTable].forEach(table => {
                OrderStop.setTableParentDataSource(table, this.mainDataSource);
                table.dataSource.mode = DataSourceMode.UPDATE;
                if (table.quickAddData) {
                    table.resetQuickAddRow();
                }
                notesRefsLayout.updateStopsTabCaption(table);
                const parentTab = table.parent as Tab;
                if (parentTab.expanded) {
                    parentTab.expanded = false;
                }
            });

            notesRefsLayout.stopRow = row;
            this.mainDataSource.displayDataInBoundComponents();
        })
    }

    private dateValidator(isArrive: boolean, isActual: boolean, arriveComp: Textbox, departComp: Textbox,
        mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (!StringUtil.isEmptyString(arriveComp.text) && !StringUtil.isEmptyString(departComp.text)) {
            const arrivalDate = DateUtil.parseDateWithKeywords(arriveComp.text, true, true);
            const departureDate = DateUtil.parseDateWithKeywords(departComp.text, true, true);
            const dayDiff = DateUtil.dateDiff(DatePart.SECOND, departureDate, arrivalDate);

            if (dayDiff < 0) {
                return {
                    component: isArrive ? arriveComp : departComp,
                    isValid: false,
                    validationMessage: isActual
                        ? 'The arrival date cannot be later than the departure date.'
                        : 'The scheduled early arrival date cannot be later than the late arrival date.'
                };
            } else {
                arriveComp.resetValidation();
                departComp.resetValidation();
            }
        }
        return null;
    }

    async stopDateOnBlur(event: BlurEvent) {
        await AppointmentUtil.checkAppointment(event);
        this.mainDataSource.displayDataInBoundComponent(this.textboxSchedArriveEarly);
        this.mainDataSource.displayDataInBoundComponent(this.textboxSchedArriveLate);
    }

    public get orderId(): string {
        return this._orderId;
    }
    public set orderId(value: string) {
        this._orderId = value;
        this.orderSource.search({id:value});
    }

    sourceStopAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            const items = this.textboxStopType.resolveItems();
            this.textboxStopType.items = items.filter((item: DropdownItem) => {
               return item.value !== "SD";
            });
        }
    }
}
